import React, { useCallback, useState, useMemo } from 'react';

import debounce from 'lodash/debounce';
import { useField } from 'react-final-form';

import { SuggestedInput as Input } from '@vk-hr-tek/ui/input';

import { compose } from '../validation';

import { SuggestedInputProps } from './SuggestedInput.types';

/**
 * Компонент ввода с подсказками.
 * @param {SuggestedInputProps & FieldProps} props - Пропсы компонента, объединяющие пропсы SuggestedInput и FieldProps из react-final-form.
 * @returns {JSX.Element} Компонент Field, который рендерит кастомный компонент ввода Input.
 */
export const SuggestedInput = (props: SuggestedInputProps) => {
  const {
    type,
    name,
    label,
    onChange,
    placeholder,
    disabled = false,
    required = false,
    clearable = false,
    loading = false,
    tooltip,
    helperText,
    renderOption,
    limit = 50,
    minInputValueLength = 1,
    mask,
    validate,
    validationWithoutTouch = false,
    validationWhenDisabled = false,
    debounceTimeout,
    ...rest
  } = props;

  const fieldProps = useField<string | undefined>(name, {
    validate: Array.isArray(validate) ? compose(validate) : validate,
    ...rest,
  });

  const { input, meta } = fieldProps;
  const { onChange: onInputChange } = input;

  const [value, setValue] = useState<string | undefined>(input.value);

  const handleChangeForm = useCallback(
    (formValue?: string) => {
      onInputChange(formValue);

      if (onChange) {
        onChange(formValue);
      }
    },
    [onInputChange, onChange],
  );

  const handleChangeDebounce = useMemo(
    () => debounce(handleChangeForm, debounceTimeout),
    [debounceTimeout, handleChangeForm],
  );

  const handleChange = useCallback(
    (changedValue?: string) => {
      setValue(changedValue);

      if (debounceTimeout) {
        handleChangeDebounce(changedValue);

        if (changedValue === '') {
          handleChangeForm(changedValue);
        }
      } else {
        handleChangeForm(changedValue);
      }
    },
    [debounceTimeout, handleChangeDebounce, handleChangeForm],
  );

  const errorDisabled = disabled || !validationWhenDisabled;

  const renderError =
    !errorDisabled && validationWithoutTouch
      ? !!input.value && !!meta.error
      : meta.touched && !!meta.error;

  const commonInputProps = {
    ...input,
    type,
    label,
    onChange: handleChange,
    placeholder,
    disabled,
    required,
    clearable,
    loading,
    tooltip,
    helperText: (renderError && meta.error) || helperText,
    renderOption,
    limit,
    minInputValueLength,
  };

  if (type === 'load') {
    return (
      <Input
        {...commonInputProps}
        value={value || input.value}
        type="load"
        loadItems={props.loadItems}
        mask={mask}
        error={renderError}
      />
    );
  }

  return (
    <Input
      {...commonInputProps}
      value={value || input.value}
      type="local"
      externalItems={props.externalItems}
      error={meta.touched && !!meta.error}
    />
  );
};
